import React, { useState, useEffect } from 'react';
import Modal from '../ui/Modal';
import SuccessMessage from '../ui/SuccessMessage';
import ErrorMessage from '../ui/ErrorMessage';
import courseService from '../../services/courseService';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

/**
 * CourseFormModal - Multi-step wizard for creating/editing courses
 * Step 1: Course Details (title, description, price, image, etc.)
 * Step 2: Modules (add multiple modules)
 * Step 3: Lessons (add lessons to modules with PDF upload)
 */
const CourseFormModal = ({ isOpen, onClose, course, onSave }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Track which course we've loaded to prevent re-fetching
  const loadedCourseIdRef = React.useRef(null);

  // Course details form
  const [courseData, setCourseData] = useState({
    title: '',
    description: '',
    price: 0,
    original_price: 0,
    duration: '',
    category: '',
    is_published: false,
    image: null
  });
  const [imagePreview, setImagePreview] = useState(null);

  // Modules data (array of modules to create)
  const [modules, setModules] = useState([]);
  const [tempModuleTitle, setTempModuleTitle] = useState('');
  const [editingModuleIndex, setEditingModuleIndex] = useState(null);
  const [editingModuleTitle, setEditingModuleTitle] = useState('');

  // Lessons data (array of lessons with module assignments)
  const [lessons, setLessons] = useState([]);
  const [tempLesson, setTempLesson] = useState({
    title: '',
    module_index: 0,
    duration_estimate: '',
    is_preview: false,
    pdf_file: null,
    pdf_file_name: ''
  });
  const [editingLessonIndex, setEditingLessonIndex] = useState(null);
  const [editingLesson, setEditingLesson] = useState({
    title: '',
    duration_estimate: '',
    is_preview: false,
    pdf_file: null,
    pdf_file_name: ''
  });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      resetForm();
      loadedCourseIdRef.current = null; // Clear loaded course ID when closing
    }
  }, [isOpen]);

  // Initialize form data when editing
  useEffect(() => {
    if (course && isOpen) {
      // Only fetch if we haven't loaded this course yet
      const shouldFetch = loadedCourseIdRef.current !== course.id;

      setCourseData({
        title: course.title || '',
        description: course.description || '',
        price: course.price || 0,
        original_price: course.original_price || 0,
        duration: course.duration || '',
        category: course.category || '',
        is_published: course.is_published || false,
        image: null
      });
      setImagePreview(course.image || null);

      // Only fetch existing modules and lessons once per course
      if (shouldFetch) {
        loadedCourseIdRef.current = course.id;
        fetchExistingModulesAndLessons();
      }
    }
  }, [course, isOpen]);

  // Fetch existing modules and lessons for editing
  const fetchExistingModulesAndLessons = async () => {
    if (!course?.id) return;

    try {
      setLoading(true);

      // Fetch course details with modules and lessons
      const courseDetails = await courseService.getCourseById(course.id);

      if (courseDetails.modules && courseDetails.modules.length > 0) {
        // Map modules to the format expected by the form
        const existingModules = courseDetails.modules.map(module => ({
          id: module.id, // Keep the ID for reference
          title: module.title,
          order: module.order
        }));
        setModules(existingModules);

        // Map lessons to the format expected by the form
        const existingLessons = [];
        courseDetails.modules.forEach((module, moduleIndex) => {
          if (module.lessons && module.lessons.length > 0) {
            module.lessons.forEach(lesson => {
              existingLessons.push({
                id: lesson.id, // Keep the ID for reference
                title: lesson.title,
                module_index: moduleIndex,
                duration_estimate: lesson.duration_estimate || '',
                is_preview: lesson.is_preview,
                order: lesson.order,
                pdf_file: null, // Can't fetch the actual file object
                pdf_file_name: 'Existing PDF' // Placeholder to show PDF exists
              });
            });
          }
        });
        setLessons(existingLessons);
      }
    } catch (err) {
      console.error('Failed to fetch course details:', err);
      setError('Failed to load existing modules and lessons. You can still edit the course details.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setCurrentStep(1);
    setCourseData({
      title: '',
      description: '',
      price: 0,
      original_price: 0,
      duration: '',
      category: '',
      is_published: false,
      image: null
    });
    setImagePreview(null);
    setModules([]);
    setLessons([]);
    setTempModuleTitle('');
    setTempLesson({
      title: '',
      module_index: 0,
      duration_estimate: '',
      is_preview: false,
      pdf_file: null,
      pdf_file_name: ''
    });
    setError('');
    setSuccessMessage('');
  };

  const handleCourseInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCourseData({
      ...courseData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCourseData({ ...courseData, image: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Add module to the list
  const handleAddModule = () => {
    if (!tempModuleTitle.trim()) {
      setError('Module title is required');
      return;
    }
    // Calculate next order: find max order from existing modules, default to -1 if empty
    const maxOrder = modules.length > 0 ? Math.max(...modules.map(m => m.order || 0)) : -1;
    const nextOrder = maxOrder + 1;

    setModules([...modules, { title: tempModuleTitle, order: nextOrder }]);
    setTempModuleTitle('');
    setError('');
  };

  const handleRemoveModule = async (index) => {
    const removedModule = modules[index];

    // Count lessons in this module
    const moduleLessons = lessons.filter(l => l.module_index === index);
    const lessonCount = moduleLessons.length;

    // Confirm deletion
    const confirmMsg = lessonCount > 0
      ? `Are you sure you want to delete "${removedModule.title}" and its ${lessonCount} lesson(s)? This action cannot be undone.`
      : `Are you sure you want to delete "${removedModule.title}"? This action cannot be undone.`;

    if (removedModule.id) {
      if (!window.confirm(confirmMsg)) {
        return;
      }

      try {
        setLoading(true);

        // Delete all lessons in this module first (if they exist in DB)
        const existingLessons = moduleLessons.filter(l => l.id);
        for (const lesson of existingLessons) {
          await courseService.deleteLesson(lesson.id);
        }

        // Then delete the module
        await courseService.deleteModule(removedModule.id);
        setSuccessMessage(`Module "${removedModule.title}" and its lessons deleted successfully!`);

        // Auto-hide success message after 3 seconds
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (err) {
        console.error('Failed to delete module:', err);
        setError(err.response?.data?.detail || 'Failed to delete module. Please try again.');
        setLoading(false);
        return; // Don't remove from UI if API call failed
      } finally {
        setLoading(false);
      }
    }

    // Remove from local state (works for both new and deleted modules)
    const newModules = modules.filter((_, i) => i !== index).map((module, idx) => ({
      ...module,
      order: idx  // Recalculate order sequentially starting from 0
    }));
    setModules(newModules);

    // Remove lessons associated with this module and recalculate module_index
    const newLessons = lessons.filter(lesson => lesson.module_index !== index)
      .map(lesson => ({
        ...lesson,
        module_index: lesson.module_index > index ? lesson.module_index - 1 : lesson.module_index
      }));
    setLessons(newLessons);
  };

  const handleStartEditModule = (index) => {
    setEditingModuleIndex(index);
    setEditingModuleTitle(modules[index].title);
  };

  const handleSaveModuleEdit = () => {
    if (!editingModuleTitle.trim()) {
      setError('Module title cannot be empty');
      return;
    }
    const newModules = [...modules];
    newModules[editingModuleIndex] = {
      ...newModules[editingModuleIndex],
      title: editingModuleTitle
    };
    setModules(newModules);
    setEditingModuleIndex(null);
    setEditingModuleTitle('');
    setError('');
  };

  const handleCancelModuleEdit = () => {
    setEditingModuleIndex(null);
    setEditingModuleTitle('');
  };

  const handlePdfChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setTempLesson({
        ...tempLesson,
        pdf_file: file,
        pdf_file_name: file.name
      });
    }
  };

  // Add lesson to the list
  const handleAddLesson = () => {
    if (!tempLesson.title.trim()) {
      setError('Lesson title is required');
      return;
    }
    if (!tempLesson.pdf_file) {
      setError('PDF file is required');
      return;
    }

    // Calculate next order for this module (0-based)
    const moduleLessons = lessons.filter(l => l.module_index === tempLesson.module_index);
    const nextOrder = moduleLessons.length > 0 ? Math.max(...moduleLessons.map(l => l.order || 0)) + 1 : 0;

    setLessons([...lessons, { ...tempLesson, order: nextOrder }]);
    setTempLesson({
      title: '',
      module_index: tempLesson.module_index,
      duration_estimate: '',
      is_preview: false,
      pdf_file: null,
      pdf_file_name: ''
    });
    setError('');
  };

  const handleRemoveLesson = async (index) => {
    const removedLesson = lessons[index];

    // If lesson has an ID, it exists in the database and needs to be deleted via API
    if (removedLesson.id) {
      if (!window.confirm(`Are you sure you want to delete "${removedLesson.title}"? This action cannot be undone.`)) {
        return;
      }

      try {
        setLoading(true);
        await courseService.deleteLesson(removedLesson.id);
        setSuccessMessage(`Lesson "${removedLesson.title}" deleted successfully!`);

        // Auto-hide success message after 3 seconds
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (err) {
        console.error('Failed to delete lesson:', err);
        setError(err.response?.data?.detail || 'Failed to delete lesson. Please try again.');
        setLoading(false);
        return; // Don't remove from UI if API call failed
      } finally {
        setLoading(false);
      }
    }

    // Remove from local state (works for both new and deleted lessons)
    const newLessons = lessons.filter((_, i) => i !== index);

    // Recalculate order for lessons in the same module (0-based)
    const updatedLessons = newLessons.map(lesson => {
      if (lesson.module_index === removedLesson.module_index) {
        const sameModuleLessons = newLessons.filter(l => l.module_index === lesson.module_index);
        const lessonIndexInModule = sameModuleLessons.indexOf(lesson);
        return {
          ...lesson,
          order: lessonIndexInModule
        };
      }
      return lesson;
    });

    setLessons(updatedLessons);
  };

  const handleStartEditLesson = (index) => {
    const lesson = lessons[index];
    setEditingLessonIndex(index);
    setEditingLesson({
      title: lesson.title,
      duration_estimate: lesson.duration_estimate || '',
      is_preview: lesson.is_preview,
      pdf_file: null, // Don't prefill file
      pdf_file_name: lesson.pdf_file_name || ''
    });
  };

  const handleSaveLessonEdit = () => {
    if (!editingLesson.title.trim()) {
      setError('Lesson title cannot be empty');
      return;
    }

    const newLessons = [...lessons];
    newLessons[editingLessonIndex] = {
      ...newLessons[editingLessonIndex],
      title: editingLesson.title,
      duration_estimate: editingLesson.duration_estimate,
      is_preview: editingLesson.is_preview,
      // Only update PDF if a new one was selected
      ...(editingLesson.pdf_file && {
        pdf_file: editingLesson.pdf_file,
        pdf_file_name: editingLesson.pdf_file_name
      })
    };
    setLessons(newLessons);
    setEditingLessonIndex(null);
    setEditingLesson({
      title: '',
      duration_estimate: '',
      is_preview: false,
      pdf_file: null,
      pdf_file_name: ''
    });
    setError('');
  };

  const handleCancelLessonEdit = () => {
    setEditingLessonIndex(null);
    setEditingLesson({
      title: '',
      duration_estimate: '',
      is_preview: false,
      pdf_file: null,
      pdf_file_name: ''
    });
  };

  const handleEditLessonPdfChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditingLesson({
        ...editingLesson,
        pdf_file: file,
        pdf_file_name: file.name
      });
    }
  };

  // Reorder modules handler
  const handleModuleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(modules);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update order property (0-based indexing)
    const updatedItems = items.map((item, index) => ({
      ...item,
      order: index
    }));

    setModules(updatedItems);

    // Update lesson module_index references
    const updatedLessons = lessons.map(lesson => {
      const oldModuleIndex = lesson.module_index;
      const newModuleIndex = updatedItems.findIndex(m =>
        modules[oldModuleIndex]?.title === m.title
      );
      return {
        ...lesson,
        module_index: newModuleIndex !== -1 ? newModuleIndex : lesson.module_index
      };
    });
    setLessons(updatedLessons);
  };

  // Reorder lessons handler
  const handleLessonDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(lessons);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Recalculate order for each lesson based on its position within its module (0-based)
    const updatedItems = items.map((item) => {
      const sameModuleLessons = items.filter(l => l.module_index === item.module_index);
      const indexInModule = sameModuleLessons.indexOf(item);
      return {
        ...item,
        order: indexInModule
      };
    });

    setLessons(updatedItems);
  };

  // Navigate steps
  const handleNext = () => {
    if (currentStep === 1) {
      // Validate course details
      if (!courseData.title.trim()) {
        setError('Course title is required');
        return;
      }
      setError('');
    }
    if (currentStep === 2 && modules.length === 0) {
      setError('Please add at least one module before proceeding');
      return;
    }
    setError('');
    setCurrentStep(currentStep + 1);
  };

  const handlePrevious = () => {
    setError('');
    setCurrentStep(currentStep - 1);
  };

  // Final save - creates course, modules, and lessons
  const handleFinalSave = async () => {
    try {
      setLoading(true);
      setError('');

      // Validate
      if (!courseData.title.trim()) {
        setError('Course title is required');
        setCurrentStep(1);
        setLoading(false);
        return;
      }
      if (modules.length === 0) {
        setError('At least one module is required');
        setCurrentStep(2);
        setLoading(false);
        return;
      }

      let savedCourse;

      // Step 1: Create/Update Course
      console.log('Creating/Updating course...', courseData);
      try {
        // Filter out null/undefined image if not provided
        const coursePayload = { ...courseData };
        if (!coursePayload.image) {
          delete coursePayload.image;
        }

        if (course?.id) {
          savedCourse = await courseService.updateCourse(course.id, coursePayload);
        } else {
          savedCourse = await courseService.createCourse(coursePayload);
        }
        console.log('Course created/updated (full response):', savedCourse);
        console.log('Course ID:', savedCourse.id);
      } catch (err) {
        console.error('Course creation failed:', err);
        throw new Error(`Failed to create course: ${err.response?.data?.detail || err.message}`);
      }

      // Check if we got an ID back
      if (!savedCourse || !savedCourse.id) {
        console.error('Course response missing ID:', savedCourse);
        throw new Error('Course was created but no ID was returned. Response: ' + JSON.stringify(savedCourse));
      }

      const courseId = savedCourse.id;
      console.log('Using course ID:', courseId);

      // Step 2: Create/Update Modules
      console.log('Creating/Updating modules...', modules);
      const createdModules = [];

      // Separate existing modules (with IDs) from new modules (without IDs)
      const existingModules = modules.filter(m => m.id);
      const newModules = modules.filter(m => !m.id);

      if (course?.id) {
        // Editing mode:
        // 1. First, update existing modules (to save title changes)
        console.log('Editing mode: found', existingModules.length, 'existing modules and', newModules.length, 'new modules');

        if (existingModules.length > 0) {
          console.log('Updating existing modules:', existingModules);
          try {
            for (const module of existingModules) {
              const modulePayload = {
                title: module.title,
                order: module.order
              };
              console.log(`Updating module ${module.id}:`, modulePayload);
              const updatedModule = await courseService.updateModule(module.id, modulePayload);
              createdModules.push(updatedModule);
              console.log('Module updated:', updatedModule);
            }
          } catch (err) {
            console.error('Module update failed:', err);
            console.error('Error response data:', err.response?.data);
            const errorDetails = err.response?.data ? JSON.stringify(err.response.data) : err.message;
            throw new Error(`Failed to update modules: ${errorDetails}`);
          }
        }

        // 2. Create any NEW modules that were added during editing
        if (newModules.length > 0) {
          console.log('Creating new modules during edit:', newModules);
          try {
            for (const module of newModules) {
              const modulePayload = {
                ...module,
                course_id: courseId  // Backend expects 'course_id'
              };
              console.log('Sending new module payload:', modulePayload);
              const createdModule = await courseService.createModule(modulePayload);
              createdModules.push(createdModule);
              console.log('New module created:', createdModule);
            }
          } catch (err) {
            console.error('New module creation failed:', err);
            console.error('Error response data:', err.response?.data);
            const errorDetails = err.response?.data ? JSON.stringify(err.response.data) : err.message;
            throw new Error(`Failed to create new modules: ${errorDetails}`);
          }
        }

        // 3. Now reorder ALL modules (existing + newly created)
        try {
          // Build ordered array from our original modules order, matching with created modules
          const orderedModuleIds = [];
          for (const module of modules) {
            // Find this module in createdModules (by title if no ID, by ID if exists)
            const found = createdModules.find(cm =>
              module.id ? cm.id === module.id : cm.title === module.title
            );
            if (found && found.id) {
              orderedModuleIds.push(found.id);
            }
          }

          if (orderedModuleIds.length > 0) {
            console.log('Reordering all modules:', orderedModuleIds);
            await courseService.reorderModules(orderedModuleIds);
            console.log('Modules reordered successfully');
          }
        } catch (err) {
          console.error('Module reorder failed:', err);
          // Don't throw error - continue with lesson updates
          console.warn('Module order update failed, but course and modules were saved');
        }
      } else {
        // Creating mode: create all modules (none exist yet)
        try {
          for (const module of modules) {
            const modulePayload = {
              ...module,
              course_id: courseId  // Backend expects 'course_id'
            };
            console.log('Sending module payload:', modulePayload);
            const createdModule = await courseService.createModule(modulePayload);
            createdModules.push(createdModule);
            console.log('Module created:', createdModule);
          }
        } catch (err) {
          console.error('Module creation failed:', err);
          console.error('Error response data:', err.response?.data);
          const errorDetails = err.response?.data ? JSON.stringify(err.response.data) : err.message;
          throw new Error(`Failed to create modules: ${errorDetails}`);
        }
      }

      // Step 3: Create Lessons (only new ones without ID)
      console.log('Creating lessons...', lessons);

      // Helper function to find module ID by index
      // Maps lesson.module_index to the correct module in createdModules
      const getModuleIdByIndex = (moduleIndex) => {
        const moduleAtIndex = modules[moduleIndex];
        if (!moduleAtIndex) return null;

        // Find matching module in createdModules by ID (if exists) or title
        const found = createdModules.find(cm =>
          moduleAtIndex.id ? cm.id === moduleAtIndex.id : cm.title === moduleAtIndex.title
        );
        return found?.id || null;
      };

      if (!course?.id) {
        // Creating mode: create all lessons
        try {
          for (const lesson of lessons) {
            const moduleId = getModuleIdByIndex(lesson.module_index);
            if (moduleId) {
              const lessonPayload = {
                title: lesson.title,
                duration_estimate: lesson.duration_estimate || '',
                is_preview: lesson.is_preview,
                order: lesson.order,
                pdf_file: lesson.pdf_file,
                module_id: moduleId  // Backend expects 'module_id'
              };
              console.log('Sending lesson payload:', lessonPayload);
              const createdLesson = await courseService.createLesson(lessonPayload);
              console.log('Lesson created:', createdLesson);
            } else {
              console.error('Failed to find module ID for lesson:', lesson);
            }
          }
        } catch (err) {
          console.error('Lesson creation failed:', err);
          console.error('Error response data:', err.response?.data);
          const errorDetails = err.response?.data ? JSON.stringify(err.response.data) : err.message;
          throw new Error(`Failed to create lessons: ${errorDetails}`);
        }
      } else {
        // Editing mode: update existing lessons, create new lessons, and reorder
        try {
          // Separate existing and new lessons
          const existingLessons = lessons.filter(l => l.id);
          const newLessons = lessons.filter(l => !l.id);

          // 1. First, update all existing lessons (to save title, duration, preview, PDF changes)
          if (existingLessons.length > 0) {
            console.log('Updating existing lessons:', existingLessons);
            for (const lesson of existingLessons) {
              const moduleId = getModuleIdByIndex(lesson.module_index);
              if (moduleId) {
                const lessonPayload = {
                  title: lesson.title,
                  duration_estimate: lesson.duration_estimate || '',
                  is_preview: lesson.is_preview,
                  order: lesson.order,
                  module_id: moduleId
                };

                // Only include pdf_file if it was changed (if there's a File object)
                if (lesson.pdf_file && lesson.pdf_file instanceof File) {
                  lessonPayload.pdf_file = lesson.pdf_file;
                }

                console.log(`Updating lesson ${lesson.id}:`, lessonPayload);
                await courseService.updateLesson(lesson.id, lessonPayload);
                console.log('Lesson updated:', lesson.id);
              }
            }
          }

          // 2. Then, create any new lessons (those without an id)
          if (newLessons.length > 0) {
            console.log('Creating new lessons:', newLessons);
            for (const lesson of newLessons) {
              const moduleId = getModuleIdByIndex(lesson.module_index);
              if (moduleId) {
                const lessonPayload = {
                  title: lesson.title,
                  duration_estimate: lesson.duration_estimate || '',
                  is_preview: lesson.is_preview,
                  order: lesson.order,
                  pdf_file: lesson.pdf_file,
                  module_id: moduleId  // Backend expects 'module_id'
                };
                console.log('Sending new lesson payload:', lessonPayload);
                const createdLesson = await courseService.createLesson(lessonPayload);
                console.log('New lesson created:', createdLesson);
              } else {
                console.error('Failed to find module ID for new lesson:', lesson);
              }
            }
          }

          // 3. Finally, reorder all lessons within each module
          // Group all lessons by module and reorder each module's lessons
          const allLessons = lessons.filter(l => l.id); // Only existing lessons have IDs to reorder
          const lessonsByModule = {};
          allLessons.forEach(lesson => {
            const moduleId = getModuleIdByIndex(lesson.module_index);
            if (moduleId) {
              if (!lessonsByModule[moduleId]) {
                lessonsByModule[moduleId] = [];
              }
              lessonsByModule[moduleId].push(lesson);
            }
          });

          // Reorder lessons for each module
          for (const [moduleId, moduleLessons] of Object.entries(lessonsByModule)) {
            const lessonIds = moduleLessons.map(l => l.id);
            if (lessonIds.length > 0) {
              console.log(`Reordering ${lessonIds.length} lessons for module ${moduleId}:`, lessonIds);
              await courseService.reorderLessons(lessonIds);
              console.log(`Lessons reordered successfully for module ${moduleId}`);
            }
          }
        } catch (err) {
          console.error('Lesson creation/update failed:', err);
          console.error('Error response data:', err.response?.data);
          // Don't throw error for reorder failures - course was still saved
          console.warn('Lesson update may have failed, but course was saved');
        }
      }

      setSuccessMessage(course?.id ? 'Course updated successfully!' : 'Course created successfully with all modules and lessons!');

      setTimeout(() => {
        onSave();
        onClose();
      }, 1500);

    } catch (err) {
      console.error('Failed to save course:', err);
      const errorMessage = err.message || err.response?.data?.detail || 'Failed to save course. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 1: return 'Course Details';
      case 2: return 'Add Modules';
      case 3: return 'Add Lessons';
      default: return '';
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={course ? 'Edit Course' : 'Create New Course'}
      size="xl"
      footer={
        <div className="flex justify-between items-center w-full">
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
            >
              Cancel
            </button>
          </div>
          <div className="flex gap-2">
            {currentStep > 1 && (
              <button
                onClick={handlePrevious}
                disabled={loading}
                className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium disabled:opacity-50"
              >
                Previous
              </button>
            )}
            {currentStep < 3 ? (
              <button
                onClick={handleNext}
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleFinalSave}
                disabled={loading}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50 flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Save & Close
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      }
    >
      {/* Progress Steps */}
      <div className="mb-8">
        <div className="relative flex items-center mb-4">
          {/* Background connecting line */}
          <div className="absolute left-0 right-0 top-1/2 h-1 bg-gray-300 -translate-y-1/2" style={{ left: '5%', right: '5%' }}></div>

          {/* Progress line (completed portion) */}
          <div
            className="absolute left-0 top-1/2 h-1 bg-blue-600 -translate-y-1/2 transition-all duration-300"
            style={{
              left: '5%',
              width: currentStep === 1 ? '0%' : currentStep === 2 ? '45%' : '90%'
            }}
          ></div>

          {/* Step circles */}
          <div className="relative flex justify-between w-full px-[5%]">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex flex-col items-center">
                <div className={`flex items-center justify-center w-12 h-12 rounded-full border-2 bg-white ${
                  currentStep >= step
                    ? 'border-blue-600'
                    : 'border-gray-300'
                }`}>
                  {currentStep > step ? (
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <span className={`font-semibold text-lg ${
                      currentStep >= step ? 'text-blue-600' : 'text-gray-400'
                    }`}>{step}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Step labels */}
        <div className="flex text-sm px-[5%]">
          <div className="flex-1 flex justify-start">
            <span className={currentStep === 1 ? 'text-blue-600 font-medium' : 'text-gray-500'}>Course Details</span>
          </div>
          <div className="flex-1 flex justify-center">
            <span className={currentStep === 2 ? 'text-blue-600 font-medium' : 'text-gray-500'}>Modules</span>
          </div>
          <div className="flex-1 flex justify-end">
            <span className={currentStep === 3 ? 'text-blue-600 font-medium' : 'text-gray-500'}>Lessons</span>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <SuccessMessage
          message={successMessage}
          onClose={() => setSuccessMessage('')}
        />
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-4">
          <ErrorMessage message={error} onRetry={null} />
        </div>
      )}

      {/* Step Content */}
      <div className="min-h-[450px]">
        {/* Step 1: Course Details */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{getStepTitle()}</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Course Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={courseData.title}
                onChange={handleCourseInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter course title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={courseData.description}
                onChange={handleCourseInputChange}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter course description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Price (₹)
                </label>
                <input
                  type="number"
                  name="price"
                  value={courseData.price}
                  onChange={handleCourseInputChange}
                  min="0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Original Price (₹)
                </label>
                <input
                  type="number"
                  name="original_price"
                  value={courseData.original_price}
                  onChange={handleCourseInputChange}
                  min="0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Duration
                </label>
                <input
                  type="text"
                  name="duration"
                  value={courseData.duration}
                  onChange={handleCourseInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., 6 months"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <input
                  type="text"
                  name="category"
                  value={courseData.category}
                  onChange={handleCourseInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Banking"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Course Image
              </label>
              <div className="flex items-center gap-4">
                {imagePreview && (
                  <div className="w-32 h-32 rounded-lg overflow-hidden border border-gray-300 bg-sky-100">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-full object-contain"
                    />
                  </div>
                )}
                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 5MB</p>
                </div>
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="is_published"
                checked={courseData.is_published}
                onChange={handleCourseInputChange}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label className="ml-2 text-sm font-medium text-gray-700">
                Publish this course (make it visible to users)
              </label>
            </div>
          </div>
        )}

        {/* Step 2: Modules */}
        {currentStep === 2 && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{getStepTitle()}</h3>

            {/* Add Module Form */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={tempModuleTitle}
                  onChange={(e) => setTempModuleTitle(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddModule()}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter module title"
                />
                <button
                  onClick={handleAddModule}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Add Module
                </button>
              </div>
            </div>

            {/* Modules List */}
            {modules.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="mt-2 text-sm text-gray-600">No modules added yet. Add your first module above.</p>
              </div>
            ) : (
              <DragDropContext onDragEnd={handleModuleDragEnd}>
                <Droppable droppableId="modules">
                  {(provided, snapshot) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className={`space-y-2 ${snapshot.isDraggingOver ? 'bg-blue-50 rounded-lg p-2' : ''}`}
                    >
                      {modules.map((module, index) => (
                        <Draggable key={`module-${index}`} draggableId={`module-${index}`} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`flex items-center justify-between bg-white border-2 rounded-lg p-4 transition-all ${
                                snapshot.isDragging
                                  ? 'border-blue-500 shadow-lg'
                                  : 'border-gray-200'
                              }`}
                            >
                              <div className="flex items-center gap-3 flex-1">
                                <div
                                  {...provided.dragHandleProps}
                                  className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-blue-600 transition-colors"
                                  title="Drag to reorder"
                                >
                                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                                  </svg>
                                </div>
                                <span className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full font-semibold text-sm">
                                  {index + 1}
                                </span>
                                {editingModuleIndex === index ? (
                                  <div className="flex items-center gap-2 flex-1">
                                    <input
                                      type="text"
                                      value={editingModuleTitle}
                                      onChange={(e) => setEditingModuleTitle(e.target.value)}
                                      onKeyPress={(e) => e.key === 'Enter' && handleSaveModuleEdit()}
                                      className="flex-1 px-3 py-1 border border-blue-500 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                      autoFocus
                                    />
                                    <button
                                      onClick={handleSaveModuleEdit}
                                      className="text-green-600 hover:bg-green-50 p-1 rounded transition-colors"
                                      title="Save"
                                    >
                                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                      </svg>
                                    </button>
                                    <button
                                      onClick={handleCancelModuleEdit}
                                      className="text-gray-600 hover:bg-gray-50 p-1 rounded transition-colors"
                                      title="Cancel"
                                    >
                                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                    </button>
                                  </div>
                                ) : (
                                  <span className="font-medium text-gray-900 flex-1">{module.title}</span>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                {editingModuleIndex !== index && (
                                  <button
                                    onClick={() => handleStartEditModule(index)}
                                    className="text-blue-600 hover:bg-blue-50 p-2 rounded-lg transition-colors"
                                    title="Edit module"
                                  >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                  </button>
                                )}
                                <button
                                  onClick={() => handleRemoveModule(index)}
                                  className="text-red-600 hover:bg-red-50 p-2 rounded-lg transition-colors"
                                  title="Remove module"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </div>
        )}

        {/* Step 3: Lessons */}
        {currentStep === 3 && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{getStepTitle()}</h3>

            {/* Add Lesson Form */}
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Module
                  </label>
                  <select
                    value={tempLesson.module_index}
                    onChange={(e) => setTempLesson({ ...tempLesson, module_index: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {modules.map((module, index) => (
                      <option key={index} value={index}>
                        {module.title}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Lesson Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={tempLesson.title}
                      onChange={(e) => setTempLesson({ ...tempLesson, title: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter lesson title"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Duration
                    </label>
                    <input
                      type="text"
                      value={tempLesson.duration_estimate}
                      onChange={(e) => setTempLesson({ ...tempLesson, duration_estimate: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., 30 mins"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    PDF File <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handlePdfChange}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  {tempLesson.pdf_file_name && (
                    <p className="text-xs text-green-600 mt-1">✓ {tempLesson.pdf_file_name}</p>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={tempLesson.is_preview}
                      onChange={(e) => setTempLesson({ ...tempLesson, is_preview: e.target.checked })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700">
                      Allow preview (free access)
                    </span>
                  </label>
                  <button
                    onClick={handleAddLesson}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                  >
                    Add Lesson
                  </button>
                </div>
              </div>
            </div>

            {/* Lessons List by Module */}
            {lessons.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <p className="mt-2 text-sm text-gray-600">No lessons added yet. Add lessons using the form above.</p>
              </div>
            ) : (
              <DragDropContext onDragEnd={handleLessonDragEnd}>
                <div className="space-y-4">
                  {modules.map((module, moduleIndex) => {
                    const moduleLessons = lessons.filter(l => l.module_index === moduleIndex);
                    if (moduleLessons.length === 0) return null;

                    return (
                      <div key={moduleIndex} className="border border-gray-200 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                          <span className="flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-600 rounded-full font-semibold text-xs">
                            {moduleIndex + 1}
                          </span>
                          {module.title}
                          <span className="text-xs text-gray-500 font-normal">({moduleLessons.length} lesson{moduleLessons.length !== 1 ? 's' : ''})</span>
                        </h4>
                        <Droppable droppableId={`lessons-module-${moduleIndex}`}>
                          {(provided, snapshot) => (
                            <div
                              {...provided.droppableProps}
                              ref={provided.innerRef}
                              className={`space-y-2 ${snapshot.isDraggingOver ? 'bg-green-50 rounded-lg p-2' : ''}`}
                            >
                              {moduleLessons.map((lesson, lessonIndex) => {
                                const globalIndex = lessons.indexOf(lesson);
                                return (
                                  <Draggable
                                    key={`lesson-${globalIndex}`}
                                    draggableId={`lesson-${globalIndex}`}
                                    index={globalIndex}
                                  >
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        className={`rounded transition-all ${
                                          snapshot.isDragging
                                            ? 'bg-white border-2 border-green-500 shadow-lg'
                                            : 'bg-gray-50 border border-transparent'
                                        } ${editingLessonIndex === globalIndex ? 'p-4' : 'p-3'}`}
                                      >
                                        {editingLessonIndex === globalIndex ? (
                                          /* EDITING MODE */
                                          <div className="space-y-3">
                                            <div className="flex items-start gap-2">
                                              <div
                                                {...provided.dragHandleProps}
                                                className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-green-600 transition-colors mt-2"
                                                title="Drag to reorder"
                                              >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                                                </svg>
                                              </div>
                                              <div className="flex-1 space-y-3">
                                                {/* Title Input */}
                                                <div>
                                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Lesson Title <span className="text-red-500">*</span>
                                                  </label>
                                                  <input
                                                    type="text"
                                                    value={editingLesson.title}
                                                    onChange={(e) => setEditingLesson({ ...editingLesson, title: e.target.value })}
                                                    className="w-full px-3 py-2 border border-blue-500 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                                    autoFocus
                                                  />
                                                </div>

                                                {/* Duration Input */}
                                                <div>
                                                  <label className="block text-xs font-medium text-gray-700 mb-1">Duration</label>
                                                  <input
                                                    type="text"
                                                    value={editingLesson.duration_estimate}
                                                    onChange={(e) => setEditingLesson({ ...editingLesson, duration_estimate: e.target.value })}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                                                    placeholder="e.g., 30 mins"
                                                  />
                                                </div>

                                                {/* PDF File Input */}
                                                <div>
                                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    PDF File {lesson.id && '(Optional - leave empty to keep current file)'}
                                                  </label>
                                                  <input
                                                    type="file"
                                                    accept=".pdf"
                                                    onChange={handleEditLessonPdfChange}
                                                    className="block w-full text-xs text-gray-500 file:mr-4 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                                  />
                                                  {editingLesson.pdf_file_name && (
                                                    <p className="text-xs text-green-600 mt-1">
                                                      {editingLesson.pdf_file instanceof File ? '✓ New: ' : 'Current: '}
                                                      {editingLesson.pdf_file_name}
                                                    </p>
                                                  )}
                                                </div>

                                                {/* Preview Checkbox */}
                                                <label className="flex items-center">
                                                  <input
                                                    type="checkbox"
                                                    checked={editingLesson.is_preview}
                                                    onChange={(e) => setEditingLesson({ ...editingLesson, is_preview: e.target.checked })}
                                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                  />
                                                  <span className="ml-2 text-sm font-medium text-gray-700">Allow preview (free access)</span>
                                                </label>

                                                {/* Action Buttons */}
                                                <div className="flex items-center gap-2 pt-2">
                                                  <button
                                                    onClick={handleSaveLessonEdit}
                                                    className="flex items-center gap-1 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors text-sm font-medium"
                                                  >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                    Save
                                                  </button>
                                                  <button
                                                    onClick={handleCancelLessonEdit}
                                                    className="flex items-center gap-1 px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors text-sm font-medium"
                                                  >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                    Cancel
                                                  </button>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        ) : (
                                          /* DISPLAY MODE */
                                          <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3 flex-1">
                                              <div
                                                {...provided.dragHandleProps}
                                                className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-green-600 transition-colors"
                                                title="Drag to reorder"
                                              >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                                                </svg>
                                              </div>
                                              <div className="flex-1">
                                                <div className="flex items-center gap-2">
                                                  <span className="font-medium text-gray-900">{lesson.title}</span>
                                                  {lesson.is_preview && (
                                                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                                                      Preview
                                                    </span>
                                                  )}
                                                </div>
                                                <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                                                  {lesson.duration_estimate && <span>{lesson.duration_estimate}</span>}
                                                  <span className="flex items-center gap-1 text-green-600">
                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                                    </svg>
                                                    {lesson.pdf_file_name}
                                                  </span>
                                                </div>
                                              </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                              <button
                                                onClick={() => handleStartEditLesson(globalIndex)}
                                                className="text-blue-600 hover:bg-blue-50 p-2 rounded transition-colors"
                                                title="Edit lesson"
                                              >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                </svg>
                                              </button>
                                              <button
                                                onClick={() => handleRemoveLesson(globalIndex)}
                                                className="text-red-600 hover:bg-red-50 p-2 rounded transition-colors"
                                                title="Remove lesson"
                                              >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                              </button>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </Draggable>
                                );
                              })}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </div>
                    );
                  })}
                </div>
              </DragDropContext>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CourseFormModal;
